import { GenericResult } from '@/app/util/channel-api'
import { publicRuntimeConfig } from '@/app/util/env'
import UAParserContext from '@/context/ua-parser'
import { IGif } from '@giphy/js-types'
import { SyntheticEvent, useContext } from 'react'
import xss from 'xss'

export const relativeUrl = (url: string) => `/${url.replace(/^(?:\/\/|[^\/]+)*\//, '')}`

export const addNoFreestarParam = (url: string): string => {
    const destination = new URL(url, 'https://giphy.com')
    destination.searchParams.set('nf', '1')
    return `${destination.pathname}${destination.search}${destination.hash}`
}

export const relativeGifClick = (gif: IGif, e: SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    e.stopPropagation()
    location.href = relativeUrl(gif.url)
}

export function getCookie(cookieName: string, cookies?: string) {
    if (!cookies) {
        if (typeof document !== 'undefined') {
            cookies = document.cookie
        } else {
            return ''
        }
    }
    const pairs = cookies.split(';')
    for (let i = 0; i < pairs.length; i++) {
        let cookie = pairs[i].trim()
        if (cookie.startsWith(cookieName + '=')) {
            return cookie.substring(cookieName.length + 1)
        }
    }
    return ''
}

export function getSearchParam(name: string, searchParams?: URLSearchParams) {
    if (!searchParams) {
        if (typeof document !== 'undefined') {
            searchParams = new URLSearchParams(location.search)
        } else {
            return null
        }
    }
    return searchParams.get(name)
}

export function getJSONPost(body?: any) {
    return {
        method: 'post',
        body: body ? JSON.stringify(body) : undefined,
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
        },
    }
}

export const appendParams = (url: URL, qs: { [key: string]: any }) => {
    Object.entries(qs).forEach(([key, value]) => {
        url.searchParams.append(key, value)
    })
    return url
}

export const giphyDecode = (str: string) => {
    return str.replace(/-/g, ' ')
}

export const giphyEncode = (str: string) => {
    return str.replace(/ /g, '-')
}

export function getIdFromSlug(slug: string): string {
    return slug.split('-').pop()!
}

export function getPost(csrftoken: string, isFormData = false) {
    const headers: Record<string, string> = {
        Accept: 'application/json',
        'X-CSRFToken': csrftoken,
        'X-Requested-With': 'XMLHttpRequest',
        credentials: 'same-origin',
    }

    if (!isFormData) {
        headers['Content-Type'] = 'application/json'
    }

    return {
        method: 'POST',
        headers,
    }
}

interface EndpointResult<T> {
    results: T[]
    next?: string | null
    previous?: string | null
}

export const convertToPagination = <T>(result: EndpointResult<T>, offset: number) => {
    const { results: data = [] } = result
    let count = data.length
    let total_count = Infinity
    let hasMoreGifs = undefined
    // parset this url 'https://giphy.com/api/v4/channels/57259768/feed?offset=20&limit=10'
    if (result.next) {
        try {
            const url = new URL(result.next)
            const params = new URLSearchParams(url.search)
            const offsetParam = params.get('offset')
            if (offsetParam) {
                const nextOffset = parseInt(offsetParam, 10)
                // setting the count will reduce complexity in the paginator as the offset
                // is calculated by offset = pagination.count + pagination.offset
                // the count is the number of gifs in the response
                // this will now represent hidden gifs
                count = nextOffset - offset
                hasMoreGifs = true
            } else {
                throw `no offset`
            }
        } catch (e) {
            console.error(`next url specified, but not parseable. error: ${e}`)
        }
    }
    // hack for hidden gifs:
    if (result.previous) {
        if (data.length === 0 || !result.next) {
            // if we're at the end, set the total_count to the offset
            total_count = offset
        }
    }

    return {
        data,
        pagination: {
            count,
            total_count,
            offset,
            hasMoreGifs,
        },
        meta: {
            msg: '',
            response_id: '',
            status: 200,
        },
    } as GenericResult<T>
}

/**
 * Sanitizes and normalizes a URL to use https.
 */
export const normalizeUrl = (value: string = '') => {
    const sanitized = xss(value).trim()
    if (!sanitized) return ''
    if (sanitized.startsWith('//')) return `https:${sanitized}`
    if (/^https?:\/\//i.test(sanitized)) return sanitized.replace(/^http:\/\//i, 'https://')
    return `https://${sanitized}`
}

export function useClientSideAPIKey() {
    const { deviceType } = useContext(UAParserContext)
    return deviceType === 'mobile' ? publicRuntimeConfig.mobileApiKey : publicRuntimeConfig.apiKey
}
